import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Toolbar } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from './useUser';
import {
  getShellAttentionText,
  getShellGreetingText,
  ShellStatusContext,
} from './shellStatus';
import {
  hasAlumCommandCenterAccess,
  INTERNAL_ROUTE_ROLES,
  normalizeRole,
} from '../auth/authorization';
import { supabase } from '../utils/supabaseClient';
import { fetchProfileCapabilities } from '../utils/aarHub';
import AdminNavDrawer from './AdminNavDrawer';
import AdminTopBar from './AdminTopBar';
import { collapsedDrawerWidth, drawerWidth, getCurrentNavItem } from './adminNavigation';

const AdminShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [showDataStewardshipNavItem, setShowDataStewardshipNavItem] = useState(true);
  const [showAlumCommandCenterNavItem, setShowAlumCommandCenterNavItem] = useState(false);
  const [shellStatus, setShellStatus] = useState(null);
  const normalizedRole = normalizeRole(profile?.role);
  const shellRole = normalizedRole === 'alum' || location.pathname.startsWith('/me/alum')
    ? 'alum'
    : normalizedRole;

  const currentNavItem = useMemo(
    () => getCurrentNavItem(location.pathname, shellRole),
    [location.pathname, shellRole]
  );
  const isAlumShell = shellRole === 'alum';
  const topBarHeight = isAlumShell ? 72 : 64;
  const shellConfig = useMemo(
    () => ({
      title: isAlumShell ? 'ALUM Portal' : currentNavItem?.label || 'Admin',
      subtitle: isAlumShell ? 'Road Home outreach tools' : 'Admin tools and reporting',
      sectionLabel: isAlumShell ? 'ALUM' : 'Admin',
      productName: 'OutreachOS',
      collapsedBadgeLabel: isAlumShell ? 'AL' : 'QR',
      profileHomePath: isAlumShell ? '/me/alum' : '/me',
      profileHomeAriaLabel: isAlumShell ? 'Go to ALUM Portal' : 'Go to My Dashboard',
    }),
    [currentNavItem?.label, isAlumShell]
  );

  useEffect(() => {
    let active = true;

    if (profile?.role !== 'admin') {
      setShowDataStewardshipNavItem(true);
      return () => {
        active = false;
      };
    }

    setShowDataStewardshipNavItem(false);

    const loadStewardshipQueueState = async () => {
      const { data, error } = await supabase.rpc('get_data_stewardship_queue_v1', {
        include_archived_assets: false,
      });

      if (!active) return;

      if (error) {
        setShowDataStewardshipNavItem(true);
        return;
      }

      const summary = data?.summary || {};
      const visibleQueueCount = Number(summary.assets_total || 0)
        + Number(summary.events_total || 0)
        + Number(summary.event_pages_total || 0);

      setShowDataStewardshipNavItem(visibleQueueCount > 0);
    };

    loadStewardshipQueueState();

    return () => {
      active = false;
    };
  }, [profile?.role]);

  useEffect(() => {
    let active = true;

    const loadAlumCommandCenterAccess = async () => {
      if (normalizedRole === 'admin') {
        setShowAlumCommandCenterNavItem(true);
        return;
      }

      if (
        !user?.id
        || !profile
        || normalizedRole === 'alum'
        || normalizedRole === 'pending'
        || normalizedRole === 'disabled'
        || !INTERNAL_ROUTE_ROLES.reporting.includes(normalizedRole)
      ) {
        setShowAlumCommandCenterNavItem(false);
        return;
      }

      setShowAlumCommandCenterNavItem(false);

      try {
        const capabilities = await fetchProfileCapabilities({ userId: user.id });
        if (!active) return;

        setShowAlumCommandCenterNavItem(
          hasAlumCommandCenterAccess({
            role: normalizedRole,
            capabilities,
          })
        );
      } catch (loadError) {
        if (!active) return;
        console.error('Failed to load ALUM Command Center navigation access', loadError);
        setShowAlumCommandCenterNavItem(false);
      }
    };

    loadAlumCommandCenterAccess();

    return () => {
      active = false;
    };
  }, [normalizedRole, profile, user?.id]);

  // The routed page publishes only a count; the toolbar decides the wording, so
  // a long dynamic sentence can never reach the header.
  const publishShellStatus = useCallback((nextStatus) => {
    setShellStatus(Number.isFinite(nextStatus?.attentionCount)
      ? { attentionCount: nextStatus.attentionCount }
      : null);
  }, []);

  const toolbarGreeting = getShellGreetingText(profile, user);
  const toolbarStatusLabel = getShellAttentionText(shellStatus?.attentionCount);

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        background: (theme) => theme.custom.pageGradient,
      }}
    >
      <AdminTopBar
        title={shellConfig.title}
        subtitle={shellConfig.subtitle}
        user={user}
        profile={profile}
        greeting={toolbarGreeting}
        statusLabel={toolbarStatusLabel}
        onMenuClick={() => setMobileOpen(true)}
        onProfileClick={() => handleNavigate(shellConfig.profileHomePath)}
        onLogout={handleLogout}
        profileHomeAriaLabel={shellConfig.profileHomeAriaLabel}
        toolbarMinHeight={topBarHeight}
      />

      <AdminNavDrawer
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        pathname={location.pathname}
        onNavigate={handleNavigate}
        collapsed={navCollapsed}
        onToggleCollapse={() => setNavCollapsed((current) => !current)}
        profile={profile}
        showDataStewardshipNavItem={showDataStewardshipNavItem}
        showAlumCommandCenterNavItem={showAlumCommandCenterNavItem}
        shellRole={shellRole}
        sectionLabel={shellConfig.sectionLabel}
        productName={shellConfig.productName}
        collapsedBadgeLabel={shellConfig.collapsedBadgeLabel}
        profileHomePath={shellConfig.profileHomePath}
        profileHomeAriaLabel={shellConfig.profileHomeAriaLabel}
        topBarHeight={topBarHeight}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${navCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          minWidth: 0,
        }}
      >
        <Toolbar sx={{ minHeight: `${topBarHeight}px` }} />
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4, lg: 6 },
            pt: isAlumShell ? { xs: 4, md: 5 } : { xs: 3, md: 4 },
            pb: { xs: 3, md: 4 },
            width: '100%',
          }}
        >
          <ShellStatusContext.Provider value={publishShellStatus}>
            <Outlet />
          </ShellStatusContext.Provider>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminShell;
